import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Loader2, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/dash/Common";
import { FullLoader } from "@/components/ProtectedRoute";
import { useSettings, brandingUrl, applyHead } from "@/context/SettingsContext";
import { api, errMsg } from "@/lib/api";

function Card({ title, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
      <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, area, testid }) {
  const Comp = area ? Textarea : Input;
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Comp value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function ImageField({ label, hint, fileId, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/branding/upload", fd);
      onChange(data.file_id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
          {fileId ? <img src={brandingUrl(fileId)} alt={label} className="h-full w-full object-contain" data-testid={`${testid}-preview`} /> : <span className="text-[10px] text-slate-400">Bawaan</span>}
        </div>
        <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm cursor-pointer hover:border-amber-400">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}Unggah
          <input type="file" accept="image/png,image/jpeg,image/webp,image/x-icon,.ico" className="hidden" onChange={upload} data-testid={`${testid}-input`} />
        </label>
        {fileId && <Button variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testid}-reset`}><X className="h-4 w-4 mr-1" />Pakai bawaan</Button>}
      </div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function ListEditor({ items, onChange, fields, empty, testid, addLabel }) {
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="rounded-md border border-slate-200 p-4 grid gap-3 sm:grid-cols-[1fr_auto]">
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map(([k, l, area]) => (
              <div key={k} className={area ? "sm:col-span-2" : ""}><Field label={l} value={it[k]} area={area} onChange={(v) => update(i, k, v)} testid={`${testid}-${i}-${k}`} /></div>
            ))}
          </div>
          <Button variant="ghost" size="icon" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-${i}-remove`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...items, empty])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />{addLabel}</Button>
    </div>
  );
}

const CONTACT_FIELDS = [["address", "Alamat"], ["whatsapp", "WhatsApp"], ["phone", "Telepon"], ["email", "Email"], ["hours", "Jam operasional"]];
const SECTION_FIELDS = [["title", "Judul bagian"], ["body", "Isi", true]];

export default function SettingsPage() {
  const { settings, setSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (settings && !form) setForm(settings);
  }, [settings, form]);
  if (!form) return <FullLoader />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (k) => (v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { ...form, payment_steps: form.payment_steps.filter((s) => s.trim()) });
      setSettings(data);
      setForm(data);
      applyHead(data);
      toast.success("Tampilan publik diperbarui");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const saveBtn = <Button onClick={save} disabled={busy} className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid="settings-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan Perubahan</Button>;
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <PageHeader eyebrow="Pengaturan" title="Tampilan publik" description="Ubah favicon, logo navbar, dan semua informasi yang tampil di halaman publik." actions={saveBtn} />
      <Card title="Identitas & ikon">
        <div className="grid sm:grid-cols-2 gap-6">
          <ImageField label="Logo / ikon navbar" hint="PNG/JPG/WEBP persegi, mis. 256×256 px." fileId={form.logo_file_id} onChange={set("logo_file_id")} testid="settings-logo" />
          <ImageField label="Favicon (ikon tab browser)" hint="PNG/ICO persegi, mis. 64×64 px." fileId={form.favicon_file_id} onChange={set("favicon_file_id")} testid="settings-favicon" />
          <Field label="Nama situs (di navbar)" value={form.site_name} onChange={set("site_name")} testid="settings-site-name" />
          <Field label="Tagline (di bawah nama)" value={form.tagline} onChange={set("tagline")} testid="settings-tagline" />
          <Field label="Judul tab browser" value={form.page_title} onChange={set("page_title")} testid="settings-page-title" />
        </div>
      </Card>
      <Card title="Beranda & halaman Program">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Label kecil beranda" value={form.home_eyebrow} onChange={set("home_eyebrow")} testid="settings-home-eyebrow" />
          <Field label="Teks tombol daftar" value={form.home_button_text} onChange={set("home_button_text")} testid="settings-home-button" />
          <div className="sm:col-span-2"><Field label="Judul beranda" value={form.home_title} onChange={set("home_title")} testid="settings-home-title" /></div>
          <Field label="Judul halaman Program" value={form.programs_title} onChange={set("programs_title")} testid="settings-programs-title" />
          <Field label="Kutipan di halaman login" value={form.auth_quote} onChange={set("auth_quote")} testid="settings-auth-quote" />
          <div className="sm:col-span-2"><Field label="Deskripsi halaman Program" area value={form.programs_description} onChange={set("programs_description")} testid="settings-programs-description" /></div>
        </div>
      </Card>
      <Card title="Kontak & footer">
        <div className="grid sm:grid-cols-2 gap-4">
          {CONTACT_FIELDS.map(([k, l]) => <Field key={k} label={l} value={form.contact[k]} onChange={setContact(k)} testid={`settings-contact-${k}`} />)}
          <Field label="Teks hak cipta" value={form.footer_copyright} onChange={set("footer_copyright")} testid="settings-footer-copyright" />
          <div className="sm:col-span-2"><Field label="Deskripsi footer" area value={form.footer_about} onChange={set("footer_about")} testid="settings-footer-about" /></div>
        </div>
      </Card>
      <Card title="Pembayaran">
        <ListEditor items={form.bank_accounts} onChange={set("bank_accounts")} fields={[["bank", "Nama bank"], ["number", "Nomor rekening"], ["holder", "Atas nama"]]} empty={{ bank: "", number: "", holder: "" }} testid="settings-banks" addLabel="Tambah rekening" />
        <div className="space-y-1.5">
          <Label>Langkah pembayaran (satu per baris)</Label>
          <Textarea rows={5} value={form.payment_steps.join("\n")} onChange={(e) => set("payment_steps")(e.target.value.split("\n"))} data-testid="settings-payment-steps" />
        </div>
      </Card>
      <Card title="Kebijakan privasi">
        <ListEditor items={form.privacy} onChange={set("privacy")} fields={SECTION_FIELDS} empty={{ title: "", body: "" }} testid="settings-privacy" addLabel="Tambah bagian" />
      </Card>
      <Card title="Syarat layanan">
        <ListEditor items={form.terms} onChange={set("terms")} fields={SECTION_FIELDS} empty={{ title: "", body: "" }} testid="settings-terms" addLabel="Tambah bagian" />
      </Card>
      <div className="flex justify-end">{saveBtn}</div>
    </div>
  );
}
